import React, { forwardRef, type ReactNode, type RefObject } from "react";

import { cn } from "src/shadcn/lib/utils";
import {
  Sheet as PrimitiveSheet,
  SheetContent as PrimitiveSheetContent,
  SheetDescription as PrimitiveSheetDescription,
  SheetFooter as PrimitiveSheetFooter,
  SheetHeader as PrimitiveSheetHeader,
  SheetTitle as PrimitiveSheetTitle,
} from "src/primitives/Sheet";

type SheetSize = "small" | "large" | "extraLarge";

/** Radix Sheet.Content props we forward via ...otherProps. */
type SheetContentProps = React.ComponentProps<typeof PrimitiveSheetContent>;

export interface SheetProps extends Omit<
  SheetContentProps,
  | "children"
  | "className"
  | "side"
  | "showCloseButton"
  | "onEscapeKeyDown"
  | "onInteractOutside"
> {
  /** Size of the sheet. */
  size?: SheetSize;
  /** Whether the sheet is open. */
  isOpen?: boolean;
  /** Callback invoked when the sheet is closed. */
  onClose?: () => void;
  /** Content rendered inside the sheet. */
  children?: ReactNode;
  /** Additional CSS class names applied to the sheet content. */
  className?: string;
  /** Close on pressing the Esc key. */
  closeOnEsc?: boolean;
  /** Show the close button. */
  closeButton?: boolean;
  /** Additional CSS class names applied to the backdrop/overlay. */
  backdropClassName?: string;
  /** Close on clicking outside the sheet. */
  closeOnOutsideClick?: boolean;
  /** Ref of the element to receive focus when the sheet opens. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Ref of the element to receive focus when the sheet closes. */
  finalFocusRef?: RefObject<HTMLElement | null>;
  /** Which side the sheet slides in from. */
  side?: "top" | "right" | "bottom" | "left";
  /** Radix Dialog `modal` prop — controls modal vs non-modal behaviour. */
  modal?: boolean;
  /** Radix Dialog `defaultOpen` prop. */
  defaultOpen?: boolean;
}

import { SIZE_CLASS_MAP } from "./constants";

interface SheetSubcomponentProps extends React.ComponentProps<"div"> {
  children?: ReactNode;
  className?: string;
}

const SheetHeader = forwardRef<HTMLDivElement, SheetSubcomponentProps>(
  ({ children, className, ...otherProps }, ref) => (
    <PrimitiveSheetHeader
      ref={ref}
      className={cn(className)}
      data-testid="sheet-header"
      {...otherProps}
    >
      {children}
    </PrimitiveSheetHeader>
  )
);
SheetHeader.displayName = "Sheet.Header";

const SheetBody = forwardRef<
  HTMLDivElement,
  SheetSubcomponentProps & { hasFooter?: boolean }
>(({ children, className, hasFooter = true, ...otherProps }, ref) => (
  <div
    ref={ref}
    data-slot="sheet-body"
    data-testid="sheet-body"
    className={cn(
      "flex flex-1 flex-col items-start justify-start overflow-y-auto px-6",
      hasFooter && "pb-0",
      className
    )}
    {...otherProps}
  >
    {children}
  </div>
));
SheetBody.displayName = "Sheet.Body";

const SheetFooter = forwardRef<HTMLDivElement, SheetSubcomponentProps>(
  ({ children, className, ...otherProps }, ref) => (
    <PrimitiveSheetFooter
      ref={ref}
      className={cn(
        "flex flex-row items-center justify-start gap-2",
        className
      )}
      data-testid="sheet-footer"
      {...otherProps}
    >
      {children}
    </PrimitiveSheetFooter>
  )
);
SheetFooter.displayName = "Sheet.Footer";

const SheetTitle = forwardRef<
  HTMLHeadingElement,
  React.ComponentProps<typeof PrimitiveSheetTitle>
>(({ children, className, ...otherProps }, ref) => (
  <PrimitiveSheetTitle
    ref={ref}
    className={cn("text-2xl font-semibold", className)}
    {...otherProps}
  >
    {children}
  </PrimitiveSheetTitle>
));
SheetTitle.displayName = "Sheet.Title";

const SheetDescription = forwardRef<
  HTMLParagraphElement,
  React.ComponentProps<typeof PrimitiveSheetDescription>
>(({ children, className, ...otherProps }, ref) => (
  <PrimitiveSheetDescription
    ref={ref}
    className={cn(className)}
    {...otherProps}
  >
    {children}
  </PrimitiveSheetDescription>
));
SheetDescription.displayName = "Sheet.Description";

const Sheet = forwardRef<HTMLDivElement, SheetProps>(
  (
    {
      size = "small",
      isOpen = false,
      onClose = () => {},
      children,
      className,
      closeOnEsc = true,
      closeButton = true,
      backdropClassName,
      closeOnOutsideClick = true,
      initialFocusRef,
      finalFocusRef,
      side = "right",
      modal,
      defaultOpen,
      // Radix content handlers — intercept and merge
      onOpenAutoFocus: onOpenAutoFocusProp,
      onCloseAutoFocus: onCloseAutoFocusProp,
      ...otherProps
    },
    ref
  ) => {
    const handleOpenChange = (open: boolean) => {
      if (!open) onClose();
    };

    const handleInteractOutside = (e: Event) => {
      if (!closeOnOutsideClick) e.preventDefault();
    };

    const handleEscapeKeyDown = (e: KeyboardEvent) => {
      if (!closeOnEsc) e.preventDefault();
    };

    const handleOpenAutoFocus = (e: Event) => {
      if (initialFocusRef) {
        e.preventDefault();
        if (initialFocusRef.current) {
          initialFocusRef.current.focus();
        } else {
          requestAnimationFrame(() => initialFocusRef.current?.focus());
        }
      }
      onOpenAutoFocusProp?.(e);
    };

    const handleCloseAutoFocus = (e: Event) => {
      if (finalFocusRef?.current) {
        e.preventDefault();
        finalFocusRef.current.focus();
      }
      onCloseAutoFocusProp?.(e);
    };

    return (
      <PrimitiveSheet
        open={isOpen}
        onOpenChange={handleOpenChange}
        modal={modal}
        defaultOpen={defaultOpen}
      >
        <PrimitiveSheetContent
          ref={ref}
          side={side}
          showCloseButton={closeButton}
          overlayClassName={backdropClassName}
          onInteractOutside={handleInteractOutside}
          onEscapeKeyDown={handleEscapeKeyDown}
          onOpenAutoFocus={handleOpenAutoFocus}
          onCloseAutoFocus={handleCloseAutoFocus}
          className={cn(SIZE_CLASS_MAP[size], className)}
          data-testid="sheet-wrapper"
          {...otherProps}
        >
          {children}
        </PrimitiveSheetContent>
      </PrimitiveSheet>
    );
  }
) as React.ForwardRefExoticComponent<
  SheetProps & React.RefAttributes<HTMLDivElement>
> & {
  Header: typeof SheetHeader;
  Body: typeof SheetBody;
  Footer: typeof SheetFooter;
  Title: typeof SheetTitle;
  Description: typeof SheetDescription;
};

Sheet.displayName = "Sheet";
Sheet.Header = SheetHeader;
Sheet.Body = SheetBody;
Sheet.Footer = SheetFooter;
Sheet.Title = SheetTitle;
Sheet.Description = SheetDescription;

export { Sheet };
